import { useQuery } from '@tanstack/react-query'
import {
  getInstrumentFundamentals,
  type InstrumentFundamental,
} from '../../api/marketData'
import { getQuote } from '../../api/trading'
import { useAuthStore } from '../../auth/useAuthStore'
import type { QuotesResponse } from '../../api/types'

interface Props {
  symbol: string
}

// ---- formatters ----

function num(v: string | number | undefined | null): number | null {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function fmtPrice(n: number | null): string {
  return n == null ? '—' : `$${n.toFixed(2)}`
}

function fmtCompactUsd(n: number | null): string {
  if (n == null) return '—'
  const abs = Math.abs(n)
  if (abs >= 1e12) return `$${(n / 1e12).toFixed(2)}T`
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(2)}M`
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(2)}K`
  return `$${n.toFixed(2)}`
}

function fmtCompact(n: number | null): string {
  if (n == null) return '—'
  const abs = Math.abs(n)
  if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`
  if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}K`
  return `${n}`
}

function fmtNum(n: number | null, digits = 2): string {
  return n == null ? '—' : n.toFixed(digits)
}

function fmtPct(n: number | null): string {
  return n == null ? '—' : `${n.toFixed(2)}%`
}

function fmtDate(s: string | null): string {
  return s && s.length > 0 ? s : '—'
}

function divFreqLabel(n: number | null): string {
  switch (n) {
    case 0:
      return 'None'
    case 1:
      return 'Annual'
    case 2:
      return 'Semi-annual'
    case 4:
      return 'Quarterly'
    case 12:
      return 'Monthly'
    case null:
      return '—'
    default:
      return `${n}×/yr`
  }
}

export function OverviewTab({ symbol }: Props) {
  const accountId = useAuthStore((s) => s.accountId)

  const fq = useQuery({
    queryKey: ['instrument-fundamentals', symbol],
    queryFn: () => getInstrumentFundamentals(symbol),
    enabled: !!accountId && !!symbol,
    staleTime: 6 * 60 * 60 * 1000,
    gcTime: 12 * 60 * 60 * 1000,
    retry: false,
  })

  // Live snapshot fields piggyback the shared quote cache (deduped with the
  // header / trade panel).
  const quoteQ = useQuery({
    queryKey: ['quote', symbol, accountId],
    queryFn: () => getQuote(symbol),
    enabled: !!accountId && !!symbol,
    staleTime: 2_000,
    select: (d: QuotesResponse) => d.quotes?.[0] ?? null,
  })

  const f: InstrumentFundamental | null = fq.data?.fundamental ?? null
  const name = fq.data?.name ?? null
  const exchange = fq.data?.exchange ?? null
  const quote = quoteQ.data

  const last = num(quote?.last)
  const prevClose = num(quote?.previousClose)
  const bid = num(quote?.bid)
  const ask = num(quote?.ask)
  const volume = num(quote?.volume)
  const chg = num(quote?.oneDayChange?.change)
  const chgPct = num(quote?.oneDayChange?.percentChange)
  const halted = (quote?.securityStatus ?? '').toLowerCase() === 'halted'

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-3 sm:px-4 py-4 pb-16">
      <div className="mx-auto w-full max-w-3xl space-y-4">
        <div className="flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <h1 className="flex items-center gap-2 text-lg font-semibold text-zinc-100">
              <span className="truncate">{name ?? symbol}</span>
              {halted && (
                <span
                  title="Trading halted"
                  className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-rose-500/15 text-rose-300 border border-rose-500/30"
                >
                  Halted
                </span>
              )}
            </h1>
            <p className="text-xs text-zinc-500">
              {symbol}
              {exchange ? ` · ${exchange}` : ''}
              {fq.data?.assetType ? ` · ${fq.data.assetType}` : ''}
            </p>
          </div>
        </div>

        {/* Snapshot — always available from the live quote. */}
        <Section title="Snapshot">
          <Stat label="Last" value={fmtPrice(last)} />
          <Stat
            label="Day change"
            value={
              chg == null
                ? '—'
                : `${chg >= 0 ? '+' : ''}${chg.toFixed(2)}${
                    chgPct != null ? ` (${chgPct.toFixed(2)}%)` : ''
                  }`
            }
            tone={chg == null ? undefined : chg >= 0 ? 'up' : 'down'}
          />
          <Stat label="Prev close" value={fmtPrice(prevClose)} />
          <Stat label="Bid" value={fmtPrice(bid)} />
          <Stat label="Ask" value={fmtPrice(ask)} />
          <Stat label="Volume" value={fmtCompact(volume)} />
          <Stat label="52W high" value={fmtPrice(f?.high52 ?? null)} />
          <Stat label="52W low" value={fmtPrice(f?.low52 ?? null)} />
          <Stat label="Avg vol (10d)" value={fmtCompact(f?.avg10DaysVolume ?? null)} />
        </Section>

        {fq.isLoading ? (
          <p className="text-sm text-zinc-500">Loading fundamentals…</p>
        ) : !f ? (
          <p className="text-sm text-zinc-500">
            No fundamentals available for {symbol}.
          </p>
        ) : (
          <>
            <Section title="Valuation">
              <Stat label="Market cap" value={fmtCompactUsd(f.marketCap)} />
              <Stat label="P/E" value={fmtNum(f.peRatio)} />
              <Stat label="PEG" value={fmtNum(f.pegRatio)} />
              <Stat label="P/B" value={fmtNum(f.pbRatio)} />
              <Stat label="Beta" value={fmtNum(f.beta)} />
              <Stat label="EPS (TTM)" value={fmtPrice(f.epsTTM ?? f.eps)} />
              <Stat label="Shares out" value={fmtCompact(f.sharesOutstanding)} />
            </Section>

            <Section title="Dividends">
              <Stat label="Dividend" value={f.divAmount == null ? '—' : `$${f.divAmount.toFixed(2)}`} />
              <Stat label="Yield" value={fmtPct(f.divYield)} />
              <Stat label="Frequency" value={divFreqLabel(f.divFreq)} />
              <Stat label="Ex-div date" value={fmtDate(f.divDate)} />
              <Stat label="Next ex-div" value={fmtDate(f.nextDivDate)} />
              <Stat label="Next pay date" value={fmtDate(f.nextDivPayDate)} />
            </Section>

            <Section title="Profitability & short interest">
              <Stat label="Gross margin" value={fmtPct(f.grossMarginTTM)} />
              <Stat label="Net margin" value={fmtPct(f.netProfitMarginTTM)} />
              <Stat label="Operating margin" value={fmtPct(f.operatingMarginTTM)} />
              <Stat label="ROE" value={fmtPct(f.returnOnEquity)} />
              <Stat label="ROA" value={fmtPct(f.returnOnAssets)} />
              <Stat label="ROI" value={fmtPct(f.returnOnInvestment)} />
              <Stat label="Short % float" value={fmtPct(f.shortIntToFloat)} />
              <Stat label="Days to cover" value={fmtNum(f.shortIntDayToCover)} />
            </Section>
          </>
        )}

        {fq.isError && (
          <p className="text-sm text-rose-400">
            Couldn’t load fundamentals. They may be unavailable for this symbol.
          </p>
        )}
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-zinc-800 bg-white/[0.02]">
      <h2 className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-zinc-400 border-b border-zinc-800">
        {title}
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-zinc-800">
        {children}
      </div>
    </section>
  )
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'up' | 'down'
}) {
  const valueColor =
    tone === 'up' ? 'text-emerald-400' : tone === 'down' ? 'text-rose-400' : 'text-zinc-100'
  return (
    <div className="bg-zinc-950 px-3 py-2">
      <div className="text-[11px] text-zinc-500">{label}</div>
      <div className={`font-mono text-sm ${valueColor}`}>{value}</div>
    </div>
  )
}
